import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from './shared/components/ui/sonner.js';
import { TooltipProvider } from './shared/components/ui/tooltip.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthErrorProvider } from './shared/contexts/AuthErrorContext.tsx';
import { AuthErrorBanner } from './shared/components/common/AuthErrorBanner';
import { getAppType } from './shared/utils/subdomain.js';
import './main.css';

// Only show the PWA install prompt on the user app (login/dashboard),
// not on the landing page or admin panel
window.addEventListener('beforeinstallprompt', (e) => {
  if (getAppType() !== 'user') {
    e.preventDefault();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load all apps to prevent loading unnecessary code
const UserApp = React.lazy(() => import('./apps/user/App'));
const AdminApp = React.lazy(() => import('./apps/admin/App'));
const LandingApp = React.lazy(() => import('./apps/landing/App'));

export const AppRouter = () => {
  const appType = getAppType();

  switch (appType) {
    case 'user':
      return (
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
          }
        >
          <UserApp />
        </Suspense>
      );
    case 'admin':
      return (
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
          }
        >
          <AdminApp />
        </Suspense>
      );
    case 'landing':
    default:
      return (
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
          }
        >
          <LandingApp />
        </Suspense>
      );
  }
};

import { ErrorBoundary } from './shared/components/common/ErrorBoundary';

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthErrorProvider>
      <TooltipProvider>
        <Toaster />
        <AuthErrorBanner />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </TooltipProvider>
    </AuthErrorProvider>
  </QueryClientProvider>
);

createRoot(document.getElementById('root')!).render(<App />);
